// 连接 Resolve Bridge，并原样保留其源值。
import { env } from '@maxus/website/constants'

// 浏览器直连用户本机 resolve bridge（与 DaVinci 同机）触发渲染。
// 通路理由：bridge 只监听 127.0.0.1，server / local-server 都不在用户本机、无法可靠获知其 IP，
// 唯有面板所在浏览器与 bridge 同机，故由浏览器直连。
// 跨源已由 bridge 端全局放行（CORS / Private Network Access）；当前为 Origin:* 且无 Allow-Credentials，
// 故此处 fetch 不带 credentials。

const BRIDGE_BASE_URL = env.VITE_RESOLVE_BRIDGE_URL || 'http://127.0.0.1:19999'

// setup_render_job 的 render_setting（snake_case，对齐 bridge 契约）。
// file_size_limit 与 bitrate_limit 二选一：仅透传非空那个；frame_rate 可选（bridge 当前不消费，占位前向兼容）。
export interface BridgeRenderSetting {
  render_file_name: string
  render_path: string
  width: number
  height: number
  frame_rate?: number
  file_size_limit?: number
  bitrate_limit?: number
  burn_in_subtitle: boolean
}

export interface BridgeRenderProfile {
  resolve_preset_url: string
  resolve_preset_name: string
  render_setting: BridgeRenderSetting
}

export interface SetupRenderJobPayload {
  // 传 null = 对 bridge 当前打开的工程生效。
  project_name: string | null
  // davinci_project_path 保存的是完整工程路径；仅在当前工程不匹配时用于定位并打开目标工程。
  project_manager_path: string | null
  timeline_name: string
  render_profile: BridgeRenderProfile
}

// 业务错误也走 HTTP 200 + 非 0 code；pydantic 入参校验失败走 HTTP 422（非信封结构）；网络失败 fetch reject。
// 写操作前的健康预检不通过（Resolve 未就绪）抛 "unhealthy"。四类都包成 ResolveBridgeError，调用方据此统一阻断提示。
export class ResolveBridgeError extends Error {
  readonly kind: 'network' | 'http' | 'envelope' | 'unhealthy' | 'project_mismatch' | 'project_unconfigured'
  readonly code?: number
  readonly httpStatus?: number

  constructor(
    kind: 'network' | 'http' | 'envelope' | 'unhealthy' | 'project_mismatch' | 'project_unconfigured',
    message: string,
    detail?: { code?: number; httpStatus?: number },
  ) {
    super(message)
    this.name = 'ResolveBridgeError'
    this.kind = kind
    this.code = detail?.code
    this.httpStatus = detail?.httpStatus
  }
}

interface BridgeEnvelope<T> {
  code: number
  msg: string
  data: T | null
}

const parseHttpErrorMessage = async (res: Response): Promise<string> => {
  const rawBody = await res.text()
  if (!rawBody) return `bridge HTTP ${res.status}`

  try {
    const body = JSON.parse(rawBody) as Record<string, unknown>
    const detail = body.msg ?? body.message ?? body.error ?? body.detail
    if (typeof detail === 'string') return detail
    if (detail != null) return JSON.stringify(detail)
  } catch {
    // 非 JSON 错误响应直接透传原始文本。
  }

  return rawBody
}

const parseEnvelope = async <T>(res: Response): Promise<T | null> => {
  if (!res.ok) {
    // 非 2xx（如 422 入参校验失败）：透传响应中的错误信息。
    throw new ResolveBridgeError('http', await parseHttpErrorMessage(res), {
      httpStatus: res.status,
    })
  }

  const envelope = (await res.json()) as BridgeEnvelope<T>
  if (envelope.code !== 0) {
    // 业务错误（Resolve 未连接 / 时间线不存在 / 目录不存在 / capability 不满足等）。
    throw new ResolveBridgeError('envelope', envelope.msg, { code: envelope.code })
  }
  return envelope.data
}

const postBridge = async <T>(path: string, body: unknown): Promise<T | null> => {
  let res: Response
  try {
    res = await fetch(`${BRIDGE_BASE_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  } catch (err) {
    // 连接失败 / bridge 未启动 / DNS 等：fetch reject。
    throw new ResolveBridgeError('network', err instanceof Error ? err.message : 'network error')
  }

  return parseEnvelope<T>(res)
}

const getBridge = async <T>(path: string): Promise<T | null> => {
  let res: Response
  try {
    res = await fetch(`${BRIDGE_BASE_URL}${path}`)
  } catch (err) {
    // 连接失败 / bridge 未启动 / DNS 等：fetch reject。
    throw new ResolveBridgeError('network', err instanceof Error ? err.message : 'network error')
  }

  return parseEnvelope<T>(res)
}

// 渲染状态：透传 DaVinci 原生 dict（PascalCase），只挑用得上的字段，其余宽松保留。
export interface BridgeRenderStatus {
  JobStatus?: string
  CompletionPercentage?: number
  [k: string]: unknown
}

// /health 契约（对齐 bridge model.py 的 HealthStatus）。
// 说明：/health 始终走 HTTP 200 + 信封 code 0；真实状态看 resolve_status.status。
//   ready=可操作；busy=Resolve 忙；error=Resolve 不可用；stopped=Resolve 未运行。
// bridge 进程本身不可达时 GET fetch 直接 reject（ResolveBridgeError("network")）。
export type BridgeResolveStatusType = 'ready' | 'busy' | 'error' | 'stopped'

export interface BridgeResolveStatus {
  status: BridgeResolveStatusType
  version?: {
    major: number
    minor: number
    patch: number
    build: number
    beta?: string
  } | null
}

export interface BridgeHealthStatus {
  resolve_status: BridgeResolveStatus
  server_status: string
  update_status?: unknown | null
}

export interface BridgeCapabilities {
  bridge_version: string
}

export interface DuplicateTimelinePayload {
  project_name: string | null
  project_manager_path: string | null
  source_timeline_name: string
  new_timeline_name: string
}

export interface OpenTimelinePayload {
  // 传 null = 对 bridge 当前打开的工程生效。
  project_name: string | null
  project_manager_path: string | null
  timeline_name: string
}

// create_timeline_using_clip：用单个片段一步建时间线（对齐 bridge model.py CreateTimelineUsingClipRequest）。
// 已存在同名时间线 → 信封 code 409；片段无法导入 → code 404。
export interface CreateTimelineUsingClipPayload {
  project_name: string | null
  project_manager_path: string | null
  new_timeline_name: string
  clip_file_path: string
  // 媒体池 bin 路径（如 "A/B/C"，缺失的 bin 会被创建）；不传 / null = 导入当前 bin。
  media_pool_path?: string | null
}

// ── Project manager（列工程 / 开工程）──────────────────────────────────────
// project_manager_path 为 Resolve 工程管理器里的目录路径；空串 = 根目录（bridge 端默认 ""）。
export type BridgeProjectManagerItemType = 'project' | 'folder'

export interface BridgeProjectManagerItem {
  name: string
  type: BridgeProjectManagerItemType
}

export interface ListProjectManagerPayload {
  // 不传 / 空串 = 列根目录。
  project_manager_path?: string
}

export interface OpenProjectPayload {
  project_name: string
  // 不传 / 空串 = 根目录下按名打开。
  project_manager_path?: string
}

// sync_comments 契约（snake_case 对齐 bridge model.py 的 ReviewComment / CommentPerson）。
// bridge 端 marker note 结构：首行 "{comment.created_by.name}: {content}"，随后每条回复追加一行
// "- {reply.created_by.name}: {reply.content}"；故顶层评论与每条回复的 created_by.name 都会被渲染，均为必填。
export interface BridgeCommentPerson {
  id?: string
  name: string
  avatar_src: string
}

export interface BridgeCommentReply {
  id?: string
  content: string
  created_at: number
  updated_at?: number | null
  // bridge 0.4.1 起 marker note 会渲染 reply.created_by.name，故必填（对齐 model.py CommentReply）。
  created_by: BridgeCommentPerson
  updated_by?: BridgeCommentPerson | null
}

export interface BridgeReviewComment {
  id: string
  is_resolved: boolean
  solved_at?: number | null
  // 视频锚点：秒（含小数）。
  target_time_start: number
  target_time_end?: number | null
  // marker note 由 bridge 端以 "\n" join 各元素。
  content: string[]
  reactions?: string[]
  reply_list?: BridgeCommentReply[]
  // 毫秒时间戳（unix epoch）。
  created_at: number
  updated_at?: number | null
  created_by: BridgeCommentPerson
  updated_by?: BridgeCommentPerson | null
}

// REPLACE：同步前清空时间线上所有评论标记再按入参重建（幂等，覆盖复制时间线带过来的旧标记）。
// UPDATE：仅更新已存在的对应标记，不动其他。
export type BridgeCommentSyncMode = 'replace' | 'update'

export interface SyncCommentsPayload {
  project_name: string | null
  project_manager_path: string | null
  timeline_name: string
  comment_list: BridgeReviewComment[]
  sync_mode?: BridgeCommentSyncMode
}

export interface BridgeCommentSyncResult {
  comment_id: string
  sync_marker_frame_id: number | null
  sync_status: 'synced' | 'updated' | 'fallback' | 'failed'
  sync_msg: string
}

const throwCommentSyncFailures = (results: BridgeCommentSyncResult[]) => {
  const messages = results
    .filter(({ sync_status }) => sync_status === 'failed')
    .map(({ sync_msg }) => sync_msg)

  if (messages.length > 0) {
    throw new ResolveBridgeError('envelope', messages.join('; '))
  }
}

// update_comment：只更新单条评论对应的标记（bridge 端内部走 sync_comments 的 UPDATE 模式，
// 找到已存在标记就更新、找不到就新建，不清空其他标记）。返回值仍是 sync 结果数组（只含这一条）。
export interface UpdateCommentPayload {
  project_name: string | null
  project_manager_path: string | null
  timeline_name: string
  comment: BridgeReviewComment
}

// 健康检查：bridge 不可达时 reject（ResolveBridgeError "network"）；可达则返回 Resolve 当前状态。
const getHealth = async (): Promise<BridgeHealthStatus> => {
  const data = await getBridge<BridgeHealthStatus>('/health')
  if (!data) {
    throw new ResolveBridgeError('envelope', 'missing health status')
  }
  return data
}

const getCapabilities = async (): Promise<BridgeCapabilities> => {
  const data = await getBridge<BridgeCapabilities>('/capabilities')
  if (!data) {
    throw new ResolveBridgeError('envelope', 'missing bridge capabilities')
  }
  return data
}

// 每个 bridge 写操作前的内置预检：bridge 不可达 → 透传 "network"；
// Resolve 未就绪（busy / error / stopped）→ 抛 "unhealthy"。调用方按 ResolveBridgeError 统一阻断提示。
const assertBridgeReady = async (): Promise<void> => {
  const health = await getHealth()
  const status = health.resolve_status.status
  if (status !== 'ready') {
    throw new ResolveBridgeError('unhealthy', `DaVinci Resolve is not ready: ${status}`)
  }
}

const requireDavinciProjectName = (projectName: string | null): string => {
  if (!projectName?.trim()) {
    throw new ResolveBridgeError('project_unconfigured', 'DaVinci project name is not configured for this project.')
  }
  return projectName
}

type ProjectSwitchConfirmation = (input: {
  currentProjectName: string
  expectedProjectName: string
}) => Promise<boolean>

let requestProjectSwitch: ProjectSwitchConfirmation | undefined

export const configureProjectSwitchConfirmation = (confirmation: ProjectSwitchConfirmation | undefined) => {
  requestProjectSwitch = confirmation
}

const getProjectManagerPath = (davinciProjectPath: string | null): string | undefined => {
  if (davinciProjectPath === null) return
  return davinciProjectPath
}

const withoutProjectManagerPath = <T extends { project_manager_path: string | null }>(payload: T) => {
  const { project_manager_path: _, ...request } = payload
  return request
}

const setupRenderJob = async (payload: SetupRenderJobPayload): Promise<string> => {
  const projectName = requireDavinciProjectName(payload.project_name)
  await assertCurrentProject(projectName, payload.project_manager_path)
  await assertBridgeReady()
  const data = await postBridge<{ render_job_id: string }>('/resolve/project/setup_render_job', {
    ...withoutProjectManagerPath(payload),
    project_name: projectName,
  })
  if (!data?.render_job_id) {
    throw new ResolveBridgeError('envelope', 'missing render_job_id')
  }
  return data.render_job_id
}

const startRendering = async (renderJobId: string): Promise<void> => {
  await assertBridgeReady()
  await postBridge(`/resolve/project/start_rendering/${renderJobId}`, {})
}

// 渲染状态轮询：高频读操作，不做健康预检（自身请求失败即暴露 bridge 不可用），避免每次轮询翻倍请求。
const getRenderStatus = async (renderJobId: string): Promise<BridgeRenderStatus> => {
  const data = await getBridge<{ render_status?: BridgeRenderStatus }>(`/resolve/project/render_status/${renderJobId}`)
  const status = data?.render_status
  if (!status) {
    throw new ResolveBridgeError('envelope', 'missing render status')
  }
  return status
}

const duplicateTimeline = async (payload: DuplicateTimelinePayload): Promise<void> => {
  const projectName = requireDavinciProjectName(payload.project_name)
  await assertCurrentProject(projectName, payload.project_manager_path)
  await assertBridgeReady()
  await postBridge('/resolve/project/duplicate_timeline', {
    ...withoutProjectManagerPath(payload),
    project_name: projectName,
  })
}

const openTimeline = async (payload: OpenTimelinePayload): Promise<void> => {
  const projectName = requireDavinciProjectName(payload.project_name)
  await assertCurrentProject(projectName, payload.project_manager_path)
  await assertBridgeReady()
  await postBridge('/resolve/project/open_timeline', {
    ...withoutProjectManagerPath(payload),
    project_name: projectName,
  })
}

const createTimelineUsingClip = async (payload: CreateTimelineUsingClipPayload): Promise<void> => {
  const projectName = requireDavinciProjectName(payload.project_name)
  await assertCurrentProject(projectName, payload.project_manager_path)
  await assertBridgeReady()
  await postBridge('/resolve/project/create_timeline_using_clip', {
    ...withoutProjectManagerPath(payload),
    project_name: projectName,
  })
}

// 将评论作为标记同步到时间线。返回每条评论的同步结果（部分失败仍走 code 0，对应项 sync_status 为 failed）。
const syncComments = async (payload: SyncCommentsPayload): Promise<BridgeCommentSyncResult[]> => {
  const projectName = requireDavinciProjectName(payload.project_name)
  await assertCurrentProject(projectName, payload.project_manager_path)
  await assertBridgeReady()
  const data = await postBridge<BridgeCommentSyncResult[]>('/resolve/project/sync_comments', {
    ...withoutProjectManagerPath(payload),
    project_name: projectName,
  })
  const results = data ?? []
  throwCommentSyncFailures(results)
  return results
}

// 更新单条评论标记。返回该评论的同步结果（数组，通常仅一项）。
const updateComment = async (payload: UpdateCommentPayload): Promise<BridgeCommentSyncResult[]> => {
  const projectName = requireDavinciProjectName(payload.project_name)
  await assertCurrentProject(projectName, payload.project_manager_path)
  await assertBridgeReady()
  const data = await postBridge<BridgeCommentSyncResult[]>('/resolve/project/update_comment', {
    ...withoutProjectManagerPath(payload),
    project_name: projectName,
  })
  const results = data ?? []
  throwCommentSyncFailures(results)
  return results
}

// ── Resolve 信息读取（bridge get_resolve_info capability）─────────────────────
// 三个只读接口：列时间线 / 取时间线属性 / 取工程设置。均走 HTTP 200 + 信封；
// 读操作不做 assertBridgeReady 预检（自身请求失败即暴露 bridge 不可用），对齐 getRenderStatus。

// get_timeline_list 的单项（对齐 bridge model.py 的 TimelineInfo）。
export interface BridgeTimelineInfo {
  timeline_index: number
  timeline_name: string
  timeline_unique_id: string
}

// Resolve 的 clip-property / metadata / setting getter 返回的标量（对齐 bridge ScalarValue）。
// bridge 端已过滤掉未设置（None / ""）的字段，故返回值里不含空字段。
export type BridgeScalarValue = string | number | boolean | null
export type BridgeTimelineProperties = Record<string, BridgeScalarValue>
export type BridgeProjectSettings = Record<string, BridgeScalarValue>

export interface GetTimelineListPayload {
  // 传 null = 对 bridge 当前打开的工程生效。
  project_name: string | null
  project_manager_path: string | null
}

export interface GetTimelinePropertiesPayload {
  project_name: string | null
  project_manager_path: string | null
  // 传 null = 取当前时间线。
  timeline_name: string | null
  // 不传 / 空 = 返回全部属性；传 key 列表 = 仅返回这些（缺失的 key 不报错，只是不出现在结果里）。
  properties_keys?: string[]
}

export interface GetProjectSettingsPayload {
  project_name: string | null
  project_manager_path: string | null
  // null = 返回全部设置；key 列表 = 仅返回这些（bridge 端该字段必填，故显式传 null）。
  settings_keys: string[] | null
}

const getTimelineList = async (payload: GetTimelineListPayload): Promise<BridgeTimelineInfo[]> => {
  const projectName = requireDavinciProjectName(payload.project_name)
  await assertCurrentProject(projectName, payload.project_manager_path)
  const data = await postBridge<BridgeTimelineInfo[]>('/resolve/project/get_timeline_list', {
    ...withoutProjectManagerPath(payload),
    project_name: projectName,
  })
  return data ?? []
}

const getTimelineProperties = async (payload: GetTimelinePropertiesPayload): Promise<BridgeTimelineProperties> => {
  const projectName = requireDavinciProjectName(payload.project_name)
  await assertCurrentProject(projectName, payload.project_manager_path)
  const data = await postBridge<BridgeTimelineProperties>('/resolve/timeline/get_timeline_properties', {
    ...withoutProjectManagerPath(payload),
    project_name: projectName,
  })
  return data ?? {}
}

const getProjectSettings = async (payload: GetProjectSettingsPayload): Promise<BridgeProjectSettings> => {
  const projectName = requireDavinciProjectName(payload.project_name)
  await assertCurrentProject(projectName, payload.project_manager_path)
  const data = await postBridge<BridgeProjectSettings>('/resolve/project/get_project_settings', {
    ...withoutProjectManagerPath(payload),
    project_name: projectName,
  })
  return data ?? {}
}

const getCurrentProjectName = async (): Promise<string> => {
  const data = await postBridge<{ project_name: string }>('/resolve/project/get_current_project_name', {})
  if (!data?.project_name) {
    throw new ResolveBridgeError('envelope', 'missing project_name')
  }
  return data.project_name
}

const assertCurrentProject = async (expectedProjectName: string, davinciProjectPath: string | null): Promise<void> => {
  const currentName = await getCurrentProjectName()
  if (currentName === expectedProjectName) return

  const projectManagerPath = getProjectManagerPath(davinciProjectPath)
  if (projectManagerPath === undefined) {
    throw new ResolveBridgeError(
      'project_mismatch',
      "The currently open DaVinci project doesn't match the configured project name.",
    )
  }
  if (
    !requestProjectSwitch ||
    !(await requestProjectSwitch({ currentProjectName: currentName, expectedProjectName }))
  ) {
    throw new ResolveBridgeError('project_mismatch', 'DaVinci project switch was canceled.')
  }
  await openProject({ project_name: expectedProjectName, project_manager_path: projectManagerPath })
}

const stopRendering = async (): Promise<void> => {
  // 停止操作必须允许 Resolve 处于 busy；若先走 ready 预检，正在渲染时反而无法调用停止接口。
  await postBridge('/resolve/project/stop_rendering', {})
}

// ── 项目管理器 ──────────────────────────────────────────────────────────────
// 列工程管理器条目：只读，不做健康预检（自身请求失败即暴露 bridge 不可用），对齐其他读操作。
const listProjectManager = async (payload: ListProjectManagerPayload = {}): Promise<BridgeProjectManagerItem[]> => {
  const data = await postBridge<BridgeProjectManagerItem[]>('/resolve/project_manager/list_project_manager', payload)
  return data ?? []
}

const openProject = async (payload: OpenProjectPayload): Promise<void> => {
  await assertBridgeReady()
  await postBridge('/resolve/project_manager/open_project', payload)
}

export interface BridgeNasFile {
  endpoint: string
  prefix: string | null
  storageKey: string
  filename: string
}

// reveal_in_finder 由仅支持 macOS 的 Resolve bridge 提供，NAS 共享按 /Volumes/<share> 挂载。
const revealInFinder = async (nasFile: BridgeNasFile): Promise<void> => {
  const clean = (value: string) => value.replace(/^\/+|\/+$/g, '')
  const filePath = `/Volumes/${[
    nasFile.endpoint,
    nasFile.prefix ?? '',
    nasFile.storageKey,
    nasFile.filename,
  ]
    .map(clean)
    .filter(Boolean)
    .join('/')}`
  await postBridge('/file/reveal_in_finder', { file_path: filePath })
}

let bridgeCommandQueue = Promise.resolve()

const BRIDGE_STATUS_REFRESH_INTERVAL_MS = 10_000

export type BridgeConnectionStatus = 'connected' | 'disconnected'
type BridgeStatusListener = () => void

let bridgeStatus: BridgeConnectionStatus = 'disconnected'
const bridgeStatusListeners = new Set<BridgeStatusListener>()

const setBridgeStatus = (status: BridgeConnectionStatus) => {
  if (bridgeStatus === status) return
  bridgeStatus = status
  for (const listener of bridgeStatusListeners) listener()
}

const subscribeStatus = (listener: BridgeStatusListener) => {
  bridgeStatusListeners.add(listener)
  return () => {
    bridgeStatusListeners.delete(listener)
  }
}

const getStatusSnapshot = () => bridgeStatus

const enqueueBridgeCommand = <Result>(run: () => Promise<Result>): Promise<Result> => {
  const result = bridgeCommandQueue.then(run, run)
  bridgeCommandQueue = result.then(
    () => undefined,
    () => undefined,
  )
  return result
}

const enqueueBridgeCall =
  <Args extends unknown[], Result>(
    call: (...args: Args) => Promise<Result>,
    { skipConnectionCheck = false }: { skipConnectionCheck?: boolean } = {},
  ) =>
  (...args: Args): Promise<Result> =>
    enqueueBridgeCommand(async () => {
      try {
        if (!skipConnectionCheck && bridgeStatus === 'disconnected') {
          throw new ResolveBridgeError('unhealthy', 'Resolve bridge is disconnected')
        }
        return await call(...args)
      } catch (error) {
        const bridgeError =
          error instanceof ResolveBridgeError
            ? error
            : new ResolveBridgeError('envelope', error instanceof Error ? error.message : String(error))
        throw bridgeError
      }
    })

const refreshBridgeStatus = async () => {
  try {
    const health = await bridge.getHealth()
    const resolveStatus = health.resolve_status.status
    setBridgeStatus(resolveStatus === 'ready' || resolveStatus === 'busy' ? 'connected' : 'disconnected')
  } catch {
    setBridgeStatus('disconnected')
  }
}

const initStatusRefresh = () => {
  let disposed = false
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  const refresh = async () => {
    await refreshBridgeStatus()
    if (!disposed) {
      timeoutId = setTimeout(
        () => void refresh(),
        BRIDGE_STATUS_REFRESH_INTERVAL_MS,
      )
    }
  }

  void refresh()
  return () => {
    disposed = true
    if (timeoutId !== undefined) clearTimeout(timeoutId)
  }
}

export const bridge = {
  BRIDGE_STATUS_REFRESH_INTERVAL_MS,

  get status() {
    return bridgeStatus
  },
  getStatusSnapshot,
  subscribeStatus,
  initStatusRefresh,
  setupRenderJob: enqueueBridgeCall(setupRenderJob),
  startRendering: enqueueBridgeCall(startRendering),
  getRenderStatus: enqueueBridgeCall(getRenderStatus),
  duplicateTimeline: enqueueBridgeCall(duplicateTimeline),
  openTimeline: enqueueBridgeCall(openTimeline),
  createTimelineUsingClip: enqueueBridgeCall(createTimelineUsingClip),
  syncComments: enqueueBridgeCall(syncComments),
  updateComment: enqueueBridgeCall(updateComment),
  getHealth: enqueueBridgeCall(getHealth, { skipConnectionCheck: true }),
  getCapabilities: enqueueBridgeCall(getCapabilities),
  getTimelineList: enqueueBridgeCall(getTimelineList),
  getTimelineProperties: enqueueBridgeCall(getTimelineProperties),
  getProjectSettings: enqueueBridgeCall(getProjectSettings),
  getCurrentProjectName: enqueueBridgeCall(getCurrentProjectName),
  assertCurrentProject: enqueueBridgeCall(assertCurrentProject),
  stopRendering: enqueueBridgeCall(stopRendering),
  listProjectManager: enqueueBridgeCall(listProjectManager),
  openProject: enqueueBridgeCall(openProject),
  revealInFinder: enqueueBridgeCall(revealInFinder),
}
